import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Chip, Tooltip } from "@mui/material";
import { Dispatch } from "react";
import { DropzoneState, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { TransferDraft, TransferDraftErrors } from "../../../entities";
import { featureFlags } from "../../../utils/featureFlags";
import {
  DropzoneContextProvider,
  useInnerDropzoneState,
} from "../../../providers/dropzoneProvider";
import {
  useOuterDropzoneState,
  useTransferDraft,
  useTransferDraftDispatch,
} from "../../../providers/transferDraft";
import {
  TransferDraftAction,
  TransferDraftActionType,
} from "../../../reducers/transferDraft";
import { TagAndValue, extractNonNestedTags } from "../../../utils/tagParser";
import { getHumanReadableFileSize } from "../../../utils/units";

function DropAndFileChipsZone() {
  const transferDraft: TransferDraft = useTransferDraft();
  const transferDraftDispatch: Dispatch<TransferDraftAction> =
    useTransferDraftDispatch();
  const innerDropZoneState: DropzoneState = useDropzone({
    noDragEventsBubbling: true,
    // so as to disable the opening of native file selector
    noClick: true,
    onDropAccepted: (acceptedFiles, dropEvent) => {
      transferDraftDispatch({
        type: TransferDraftActionType.filesAdd,
        addedFiles: acceptedFiles,
      });
    },
  });

  const isDragAccept: boolean = useOuterDropzoneState().isDragAccept;

  // TODO: Remove this variable when lifting the flag `REACT_APP_FF_NO_FILES_TRANSFER`
  const isFileCountInvalid: boolean =
    !featureFlags.noFilesTransfer &&
    transferDraft.errors.has(TransferDraftErrors.invalidFileCount);

  const isDropzoneEmpty: boolean = !transferDraft.files.length;

  const isOuterDragAccept: boolean = isDragAccept;
  const isInnerDragAccept: boolean = innerDropZoneState.isDragAccept;

  // `true` if the user is dragging file/s within the dropzone.
  const isDragWithinDropzone: boolean = !isOuterDragAccept && isInnerDragAccept;

  // `true` if the user is dragging file/s outside the dropzone.
  const isDragOutsideDropzone: boolean =
    isOuterDragAccept && !isInnerDragAccept;

  var colorBorder: string | undefined;
  var colorBackground: string;
  if (isDragWithinDropzone) {
    colorBorder = "var(--dropzone-on-drag-border-color)";
    colorBackground = "var(--dropzone-on-drag-bg-color)";
    // TODO: Remove this else-if when lifting the flag `REACT_APP_FF_NO_FILES_TRANSFER`
  } else if (isFileCountInvalid) {
    colorBorder = "var(--dropzone-error-border-color)";
    colorBackground = "var(--dropzone-error-bg-color)";
  } else if (isDragOutsideDropzone) {
    // tailwind color 'slate-400'
    colorBorder = "rgb(148 163 184)";
    colorBackground = "var(--dropzone-bg-color)";
  } else {
    colorBorder = undefined;
    colorBackground = "var(--dropzone-bg-color)";
  }

  return (
    <DropzoneContextProvider innerDropzoneState={innerDropZoneState}>
      <div
        // theme-depending styles
        style={{
          // text color
          color: "var(--dropzone-text-color)",

          // border color
          ...(colorBorder !== undefined
            ? { borderColor: `${colorBorder}` }
            : {}),

          // background color
          backgroundColor: `${colorBackground}`,
        }}
        className={`rounded-[3px] border-[3px] border-dashed transition duration-300 ${
          /* TODO: Remove this line when lifting the flag `REACT_APP_FF_NO_FILES_TRANSFER` */
          isFileCountInvalid
            ? "animate-dropzone-background-color animate-dropzone-border-color"
            : ""
        } ${
          isDropzoneEmpty
            ? "flex items-center justify-center gap-x-5 p-[15px] px-11"
            : "flex-warp p-1"
        } `}
        {...innerDropZoneState.getRootProps()}
      >
        <input {...innerDropZoneState.getInputProps()} />
        {isDropzoneEmpty ? <EmptyDropzoneContent /> : <LoadedDropzoneContent />}
      </div>
    </DropzoneContextProvider>
  );
}

/**
 * Empty Dropzone.
 *
 * @param emptyDropzoneProp
 * @returns a JSX element representing empty dropzone.
 */
const EmptyDropzoneContent = () => {
  const isDragAccept: boolean = useInnerDropzoneState()!.isDragAccept;
  return (
    <div className="flex flex-row items-center justify-center gap-x-[21px]">
      <DropIcon isDragAccept={isDragAccept} />
      <TextContent />
    </div>
  );
};

/**
 * Dropzone loaded with files & remove all button.
 *
 * @returns a JSX element representing dropzone with added files.
 */
const LoadedDropzoneContent = () => (
  <>
    <AddedFilesChips />
    <RemoveAllFiles />
  </>
);

/**
 * Added files in material chip form.
 *
 * @returns a JSX element representing added files.
 */
const AddedFilesChips = () => {
  const transferDraft = useTransferDraft();
  const transferDraftDispatch = useTransferDraftDispatch();
  return (
    <div>
      {transferDraft.files.map((file) => (
        <Tooltip title={file[0].name} placement="top">
          <Chip
            key={file[0].id}
            deleteIcon={
              <div className="flex h-[13px] w-[13px] items-center justify-center rounded-full bg-white">
                <FontAwesomeIcon
                  icon={faXmark as IconProp}
                  className="h-[10.5px] w-[10.5px] text-[#1A1A1A]"
                />
              </div>
            }
            label={
              <div className="flex flex-row">
                <div className="truncate">{file[0].name}</div>
                <div>&nbsp;{getHumanReadableFileSize(file[0].size)}</div>
              </div>
            }
            //todo define colors in a central place.
            sx={{ backgroundColor: "#E6E6E6", margin: 0.7 }}
            onDelete={() =>
              transferDraftDispatch({
                type: TransferDraftActionType.filesRemove,
                removedFiles: [file],
              })
            }
          />
        </Tooltip>
      ))}
    </div>
  );
};

/**
 * Removes all added files.
 *
 * @returns a JSX element representing a button to remove all
 * the files added by the sender.
 */
const RemoveAllFiles = () => {
  const { t } = useTranslation();
  const transferDraft = useTransferDraft();
  const transferDraftDispatch = useTransferDraftDispatch();
  return (
    <div className="flex justify-end">
      <Button
        variant="text"
        sx={{
          paddingX: "15px",
          ":hover": { backgroundColor: "#E6E6E6" },
          borderRadius: "10px",
          fontWeight: 400,
        }}
        onClick={() => {
          transferDraftDispatch({
            type: TransferDraftActionType.filesRemove,
            removedFiles: transferDraft.files,
          });
        }}
      >
        <p className="text-[11px]">{t(`Remove all`)}</p>
      </Button>
    </div>
  );
};

type DropIconProp = {
  isDragAccept: boolean;
};

/**
 * Drop icon SVG for [EmptyDropzoneContent] component.
 *
 * @returns a JSX element representing the drop icon SVG.
 */
const DropIcon = ({ isDragAccept }: DropIconProp) => {
  // Use primary color when dragging, otherwise use default colors
  const strokeColor = isDragAccept ? "var(--primary-color)" : "#b3b3b3";
  // Only the outline (hand-rock-light) changes color, solid part stays white
  const handOutlineColor = isDragAccept ? "var(--primary-color)" : "#b3b3b3";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="43"
      height="40"
      viewBox="0 0 43 40"
      className="flex-shrink-0"
    >
      <g id="icon" transform="translate(-92.627 0.2)">
        <rect
          id="Rechteck_497"
          data-name="Rechteck 497"
          width="29"
          height="29"
          rx="6"
          transform="translate(106.627 10.8)"
          fill="#dcdcdc"
        />
        <g
          id="Rechteck_498"
          data-name="Rechteck 498"
          transform="translate(92.627 -0.2)"
          fill="#fff"
          stroke={strokeColor}
          strokeWidth="1.5"
          strokeDasharray="1.5 1.5"
        >
          <rect width="28" height="29" rx="6" stroke="none" />
          <rect
            x="0.75"
            y="0.75"
            width="26.5"
            height="27.5"
            rx="5.25"
            fill="none"
          />
        </g>
        <path
          id="hand-rock-solid"
          d="M17.793,33.832a1.837,1.837,0,0,0-1.868,1.837h-.306V34.475a1.861,1.861,0,0,0-1.807-1.868,1.837,1.837,0,0,0-1.868,1.838V35.67h-.306V33.863a1.861,1.861,0,0,0-1.807-1.868,1.837,1.837,0,0,0-1.868,1.838V35.67H7.656V34.475a1.861,1.861,0,0,0-1.807-1.868,1.837,1.837,0,0,0-1.868,1.838v5.206l-.306-.272V37.538A1.861,1.861,0,0,0,1.868,35.67,1.837,1.837,0,0,0,0,37.507v2.542A3.684,3.684,0,0,0,1.233,42.8l4.276,3.8a1.846,1.846,0,0,1,.616,1.374v.256a.917.917,0,0,0,.919.919h9.187a.917.917,0,0,0,.919-.919v-.111a3.729,3.729,0,0,1,.287-1.428l1.876-4.452a3.659,3.659,0,0,0,.287-1.428V35.7A1.858,1.858,0,0,0,17.793,33.832Z"
          transform="translate(107.752 -18.694)"
          fill="#fff"
        />
        <path
          id="hand-rock-light"
          d="M16.1,34a2.644,2.644,0,0,0-4.047-1.017,2.643,2.643,0,0,0-4.175.087,2.637,2.637,0,0,0-4.192,2.1v.394A2.636,2.636,0,0,0,0,37.959v2.317a4.267,4.267,0,0,0,1.461,3.208l3.953,3.462c.331.29.25.592.25,1.616A.937.937,0,0,0,6.6,49.5H16.8a.937.937,0,0,0,.937-.937,4.208,4.208,0,0,1,.191-1.87l1.733-4.055A4.221,4.221,0,0,0,20,40.969V36.313A2.644,2.644,0,0,0,16.1,34ZM6.237,46.006,2.284,42.544A3.017,3.017,0,0,1,1.25,40.277V37.959A1.374,1.374,0,0,1,2.661,36.6a1.394,1.394,0,0,1,1.363,1.389v1.678a.313.313,0,0,0,.107.235l.283.248a.312.312,0,0,0,.518-.235V35.169a1.387,1.387,0,0,1,2.773.028v1.088a.313.313,0,0,0,.313.313H8.3a.313.313,0,0,0,.313-.312V34.611a1.387,1.387,0,0,1,2.773.028v1.647a.313.313,0,0,0,.313.313h.283a.313.313,0,0,0,.313-.312V35.03a1.387,1.387,0,0,1,2.773.028v1.228a.313.313,0,0,0,.313.313h.283a.313.313,0,0,0,.312-.313,1.387,1.387,0,0,1,2.773.028v4.657a2.978,2.978,0,0,1-.241,1.177L16.776,46.2a4.5,4.5,0,0,0-.291,2.049H6.914A2.512,2.512,0,0,0,6.237,46.006Z"
          transform="translate(107.752 -18.699)"
          fill={handOutlineColor}
        />
      </g>
    </svg>
  );
};

/**
 * Text content of [EmptyDropzoneContent] component.
 *
 * @returns a JSX element containing a file picker button.
 */
const TextContent = () => {
  const { t } = useTranslation();
  const innerDropZoneState: DropzoneState = useInnerDropzoneState()!;
  const transferDraft: TransferDraft = useTransferDraft();
  const dropzoneText: string = t(
    `Select <f>files</f> or drag them onto this area`
  );

  // TODO: Remove this block when lifting the flag `REACT_APP_FF_NO_FILES_TRANSFER`
  // Keep the else value
  const isDragAccept: boolean = innerDropZoneState.isDragAccept;
  const invalidFileCount =
    !featureFlags.noFilesTransfer &&
    transferDraft.errors.has(TransferDraftErrors.invalidFileCount);
  const textButtonColor: string =
    invalidFileCount && !isDragAccept
      ? "var(--dropzone-error-file-selector-text-color)"
      : "var(--dropzone-file-selector-text-color)";

  const extractedTagsAndValues: TagAndValue[] =
    extractNonNestedTags(dropzoneText);

  const elements = extractedTagsAndValues.map((extractedTagAndValue, index) => {
    switch (extractedTagAndValue.tag) {
      case "f":
        return (
          <span
            key={index}
            style={{ color: textButtonColor }}
            className={"cursor-pointer font-medium"}
            onClick={innerDropZoneState.open}
          >
            {extractedTagAndValue.value}
          </span>
        );
      case null:
        return (
          <span
            key={index}
            style={{ color: "var(--dropzone-text-color)" }}
            className="text-[15px]"
          >
            {extractedTagAndValue.value}
          </span>
        );
      default:
        return <></>;
    }
  });

  return <div className="text-wrap">{elements.map((e) => e)}</div>;
};

export { DropAndFileChipsZone, AddedFilesChips };
